import type { GameEvent } from '../types';

type AudioWindow = Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext };

export class SoundManager {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;

  public async start(volume: number): Promise<void> {
    if (!this.context) {
      const Context = window.AudioContext ?? (window as AudioWindow).webkitAudioContext;
      if (!Context) return;
      this.context = new Context();
      this.master = this.context.createGain();
      this.master.connect(this.context.destination);
    }
    this.master?.gain.setValueAtTime(volume, this.context.currentTime);
    if (this.context.state === 'suspended') await this.context.resume();
    this.tone(440, 0.08, 'square', 0.18, volume);
  }

  public play(event: GameEvent, volume: number): void {
    if (!this.context || !this.master || volume <= 0) return;
    this.master.gain.setTargetAtTime(volume, this.context.currentTime, 0.02);
    switch (event.type) {
      case 'COUNTDOWN_TICK': this.tone(520 + event.value * 90, 0.08, 'square', 0.16, volume); break;
      case 'KEY_CORRECT': this.tone(680, 0.045, 'square', 0.11, volume); break;
      case 'KEY_WRONG': this.tone(130, 0.09, 'sawtooth', 0.13, volume); break;
      case 'WORD_DESTROYED':
        this.tone(780, 0.09, 'square', 0.14, volume);
        this.tone(1050, 0.14, 'triangle', 0.12, volume, 0.07);
        break;
      case 'WORD_MISSED': this.tone(180, 0.24, 'sawtooth', 0.16, volume); break;
      case 'GAME_OVER': this.tone(110, 0.45, 'triangle', 0.18, volume); break;
      default: break;
    }
  }

  private tone(frequency: number, duration: number, wave: OscillatorType, level: number, volume: number, delay = 0): void {
    if (!this.context || !this.master) return;
    const time = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = wave; oscillator.frequency.setValueAtTime(frequency, time);
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, level * volume), time + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    oscillator.connect(gain); gain.connect(this.master); oscillator.start(time); oscillator.stop(time + duration + 0.02);
  }
}
